import Router, { useRouter } from "next/router";
import React from "react";

import type { ScheduleModalProps } from "@/content-V2/components/Finder/FinderList/ScheduleModal";
import type { FinderBlockProps, SearchProps } from "@/content-V2/types";
import { useExperience } from "@/context/ExperienceContext";
import { useTrackAppState } from "@/context/TrackAppStateContext";
import { useMapContext } from "@/finder/googleMaps/GoogleMapsProvider";
import fetchLocations from "@/finder/utils/fetchLocations";
import getPlaceFromId from "@/finder/utils/getPlaceFromId";
import getPlaceFromReverseGeocode from "@/finder/utils/getPlaceFromReverseGeocode";
import { isAcipState } from "@/finder/utils/isAcipState";
import type { Country } from "@/types/Country";
import type { FinderLocation } from "@/types/FinderLocation";

import { getFinderCountryConfig } from "../../utils";
import { useVisibleSearchResults } from "../useVisibleSearchResults";
import { useIsMobile } from "./useIsMobile";
import type { LocationDropdownOption } from "./useLocationSearch";

export type ResultsView = "mobileMap" | "mobileList" | "desktop";

export interface Place {
  address: string;
  id: string;
  latitude: number;
  longitude: number;
}

export type Vaccine =
  | "spikevax"
  | "mnexspike"
  | "mresvia"
  | "mflusiva"
  | "jp-spikevax-2526-infant_toddler"
  | "jp-spikevax-2526-child"
  | "jp-spikevax-2526-adult";

interface VaccineProductConfigSingleProduct {
  singleProduct: string[];
  products?: never;
}

interface VaccineProductConfigWithVaccines {
  products: Partial<Record<Vaccine, string[]>>;
  singleProduct?: never;
}

export type VaccineProductConfigProps =
  | VaccineProductConfigWithVaccines
  | VaccineProductConfigSingleProduct;

interface FinderContextValue {
  allSearchResults: FinderLocation[];
  country: Country;
  formRef: React.RefObject<HTMLFormElement> | null;
  hasVisibleExpandedResults: boolean;
  isMobile: boolean;
  loadMoreTemplate?: FinderBlockProps["loadMoreTemplate"];
  locationHovered: FinderLocation | null;
  locationHoveredSet: (location: FinderLocation | null) => void;
  locationSearchOption: LocationDropdownOption | null;
  locationSearchOptionSet: (option: LocationDropdownOption) => void;
  locationSelected: FinderLocation | null;
  locationSelectedSet: (location: FinderLocation) => void;
  mapBasedSearch: (searchMarker: HTMLDivElement | null, isMobileView: boolean) => void;
  mobileNavLocalizedHeaders: FinderBlockProps["mobileNavLocalizedHeaders"];
  noResultsFound: boolean;
  noResultsText: string;
  place: Place | null;
  products?: VaccineProductConfigWithVaccines["products"];
  productsFromResults: string[];
  resultsView: ResultsView;
  resultsViewSet: React.Dispatch<React.SetStateAction<ResultsView>>;
  scheduleModalProps: ScheduleModalProps;
  scheduleNowTextTemplate: string;
  searchId?: string;
  searching: boolean;
  searchThisAreaText?: string;
  searchValue: string;
  searchValueSet: React.Dispatch<React.SetStateAction<string>>;
  selectedVaccines?: Vaccine[];
  selectedVaccinesSet?: (vaccines: Vaccine[]) => void;
  setNumExtraVisibleResults: React.Dispatch<React.SetStateAction<number>>;
  showAcipModal: boolean;
  showAcipModalSet: React.Dispatch<React.SetStateAction<boolean>>;
  showSearchPrompt: boolean;
  showPharmacyLogos: boolean;
  markUserFormInteraction: () => void;
  vaccineDropdownOptions?: SearchProps["vaccineDropdownOptions"];
  vaccineValidationError?: string;
  visibleSearchResults: FinderLocation[];
  setSearchResultsManually: (
    results: FinderLocation[],
    options?: {
      place?: Place | null;
      productsSelected?: string[];
      searchId?: string;
    }
  ) => void;
}

const FinderContext = React.createContext<FinderContextValue>({
  allSearchResults: [],
  country: "us",
  formRef: null,
  hasVisibleExpandedResults: false,
  isMobile: false,
  loadMoreTemplate: { plural: "", singular: "" },
  locationHovered: null,
  locationHoveredSet: () => {},
  locationSearchOption: null,
  locationSearchOptionSet: () => {},
  locationSelected: null,
  locationSelectedSet: () => {},
  mapBasedSearch: () => {},
  mobileNavLocalizedHeaders: { mobileList: "", mobileMap: "" },
  noResultsFound: false,
  noResultsText: "",
  place: null,
  products: undefined,
  productsFromResults: [],
  resultsView: "desktop",
  resultsViewSet: () => {},
  scheduleModalProps: {
    buttonText: "",
    subBlockText: null,
    mainBlockText: null,
    title: ""
  },
  scheduleNowTextTemplate: "",
  searching: false,
  searchThisAreaText: "",
  searchValue: "",
  searchValueSet: () => {},
  selectedVaccines: [],
  selectedVaccinesSet: () => {},
  setNumExtraVisibleResults: () => {},
  showAcipModal: false,
  showAcipModalSet: () => {},
  showSearchPrompt: false,
  showPharmacyLogos: false,
  markUserFormInteraction: () => {},
  visibleSearchResults: [],
  setSearchResultsManually: () => {}
});

const updateUrlWithQueryParams = (vaccinesString: string, placeId: string) => {
  const productsQuery = vaccinesString.length ? { vaccines: vaccinesString } : {};

  return new Promise<void>((resolve) => {
    Router.push(
      {
        pathname: location.pathname,
        query: {
          ...Router.query,
          placeId: placeId,
          ...productsQuery
        }
      },
      undefined,
      { scroll: false }
    ).then(() => resolve());
  });
};

interface FinderResults {
  allSearchResults: FinderLocation[];
  productsSelected: string[];
  place: Place | null;
}

const ACIP_PRODUCTS = ["-covid", "-mnexspike"];

export const FinderContextProvider: React.FC<
  React.PropsWithChildren<{
    defaultSelectedVaccines?: Vaccine[];
    loadMoreTemplate?: FinderBlockProps["loadMoreTemplate"];
    mobileNavLocalizedHeaders: FinderBlockProps["mobileNavLocalizedHeaders"];
    noResultsText: string;
    scheduleModalProps?: ScheduleModalProps;
    scheduleNowTextTemplate?: string;
    searchIndex: "branded" | "stg-branded";
    searchThisAreaText?: string;
    showPharmacyLogos?: boolean;
    vaccineDropdownOptions?: SearchProps["vaccineDropdownOptions"];
    vaccineProductConfig: VaccineProductConfigProps;
  }>
> = ({
  children,
  defaultSelectedVaccines,
  loadMoreTemplate,
  mobileNavLocalizedHeaders,
  noResultsText,
  scheduleModalProps = {
    buttonText: "",
    subBlockText: null,
    mainBlockText: null,
    title: ""
  },
  scheduleNowTextTemplate = "Schedule on {hostname}",
  searchIndex = "branded",
  searchThisAreaText,
  showPharmacyLogos = false,
  vaccineDropdownOptions,
  vaccineProductConfig
}) => {
  const { products, singleProduct } = vaccineProductConfig;
  const usingVaccineFilters =
    !singleProduct && vaccineDropdownOptions && vaccineDropdownOptions.length > 0 && products;

  const isMobile = useIsMobile();
  const { country: experienceCountry, language } = useExperience();
  const country = experienceCountry || "us";
  const { setHasPanned, map } = useMapContext();
  const [resultsView, resultsViewSet] = React.useState<"mobileMap" | "mobileList" | "desktop">(
    isMobile ? "mobileMap" : "desktop"
  );
  const [locationSelected, locationSelectedSet] = React.useState<FinderLocation | null>(null);
  const [locationHovered, locationHoveredSet] = React.useState<FinderLocation | null>(null);
  const [place, placeSet] = React.useState<null | Place>(null);
  const [searching, searchingSet] = React.useState(false);
  const [locationSearchOption, locationSearchOptionSet] =
    React.useState<LocationDropdownOption | null>(null);
  const [searchValue, searchValueSet] = React.useState("");
  const [showAcipModal, showAcipModalSet] = React.useState(false);
  const hasSeenAcipModal = React.useRef(false);
  const hasUserInteractedWithForm = React.useRef(false);
  const formRef = React.useRef<HTMLFormElement>(null);

  const markUserFormInteraction = React.useCallback(() => {
    hasUserInteractedWithForm.current = true;
  }, []);

  // Function to manually trigger form submission for tracking purposes
  const triggerFormSubmit = React.useCallback(() => {
    if (formRef.current) {
      formRef.current.dispatchEvent(
        new Event("submit", {
          cancelable: true,
          bubbles: true
        })
      );
    }
  }, []);

  const [selectedVaccines, selectedVaccinesSet] = React.useState<Vaccine[]>(
    defaultSelectedVaccines ??
      vaccineDropdownOptions?.map((option) => option.value as Vaccine) ??
      []
  );

  const { query } = useRouter();

  const selectedVaccinesString = selectedVaccines?.join(",") || "";

  // Scroll to the bottom of the page when a location is selected in mobile map view
  // This is to ensure that the selected location's details are visible, and it's apparent to the user that there is content below the map,
  // in case the selected location details are rendered below their current viewport.
  React.useEffect(() => {
    if (locationSelected && resultsView === "mobileMap") {
      window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: "smooth" });
    }
  }, [locationSelected, resultsView]);

  const productsForSearch = React.useMemo(() => {
    if (usingVaccineFilters) {
      const selectedProducts = selectedVaccines.flatMap((vaccine) => products[vaccine] || []);
      return selectedProducts.join(",");
    } else {
      return singleProduct?.join(",") ?? "";
    }
  }, [singleProduct, selectedVaccines, usingVaccineFilters, products]);

  const [numExtraVisibleResults, setNumExtraVisibleResults] = React.useState(0);
  const [finderResults, finderResultsSet] = React.useState<FinderResults>({
    allSearchResults: [],
    productsSelected: [],
    place: null
  });
  const [hasLoadedQueryParams, hasLoadedQueryParamsSet] = React.useState<boolean>(false);
  const searchId = React.useRef<string | undefined>();
  const hasRunFirstSearch = React.useRef(false);

  const { size } = React.useMemo(() => getFinderCountryConfig(country), [country]);

  const vaccineValidationError = React.useMemo(() => {
    if (usingVaccineFilters && selectedVaccines.length === 0) {
      return "Please select at least one";
    }
  }, [selectedVaccines.length, usingVaccineFilters]);

  const canSearch = !!(place?.latitude && place?.longitude && place.id && !vaccineValidationError);

  const setPlaceFromPlaceId = React.useCallback(
    async (placeId: string) => {
      if (placeId && country && language) {
        let newPlace;
        if (placeId.startsWith("map_")) {
          newPlace = getPlaceFromReverseGeocode(placeId);
        } else {
          newPlace = await getPlaceFromId(placeId, country, language);
        }
        if (newPlace) {
          placeSet(newPlace);
          return newPlace;
        }
        return;
      }
    },
    [country, language]
  );

  // Update resultsView & map config based on device type
  React.useEffect(() => {
    if (!isMobile && resultsView !== "desktop") {
      resultsViewSet("desktop");
      map?.setOptions({ gestureHandling: "greedy" });
    } else if (isMobile && resultsView === "desktop") {
      resultsViewSet("mobileMap");
      map?.setOptions({ gestureHandling: "cooperative" });
    }
  }, [isMobile, map, resultsView, resultsViewSet]);

  // If there are query params to pull from url on first load, add them to finder state
  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (country && language) {
      hasLoadedQueryParamsSet(true);

      const queryPlaceId = searchParams.get("placeId");
      const queryVaccines = searchParams.get("vaccines")?.split(",");

      if (queryVaccines && queryVaccines.length > 0) {
        selectedVaccinesSet(queryVaccines as Vaccine[]);
      }
      if (queryPlaceId) {
        setPlaceFromPlaceId(queryPlaceId).then((newPlace) => {
          if (newPlace) {
            locationSearchOptionSet({ label: newPlace.address, value: newPlace.id });
          }
        });
      }
    }
  }, [country, language, setPlaceFromPlaceId, query]);

  // THE callback to run a search and update locations
  const runSearch = React.useCallback(() => {
    if (canSearch) {
      searchingSet(true);
      hasRunFirstSearch.current = true;
      if (hasUserInteractedWithForm.current) {
        triggerFormSubmit();
        hasUserInteractedWithForm.current = false;
      }

      const searchParams = new URLSearchParams({
        country: country as string,
        lat: String(place.latitude),
        lon: String(place.longitude),
        products: productsForSearch,
        type: searchIndex
      });

      const getLocations = async () => {
        let results: FinderLocation[] = [];
        try {
          const resolvedLocations: FinderLocation[] = await fetchLocations(searchParams).catch(
            () => {
              return [];
            }
          );

          // Sort locations by distance and return the first "size" locations - where size is the total number of locations we can display.
          // This is necessary because in the unbranded finder, we fetch from our datastore up to 30 results, and then also we fetch from walgreens API
          // sometimes when we combine the two, we get more results than we can show.
          const sortedAndSlicedResolvedLocations = resolvedLocations
            .sort((a, b) => a.location.distance - b.location.distance)
            .slice(0, size);

          await updateUrlWithQueryParams(selectedVaccinesString, place.id);

          results = sortedAndSlicedResolvedLocations;
        } finally {
          searchId.current = searchParams.toString();
          setHasPanned(false); // Reset the panning state
          searchingSet(false); // Ensure the searching state is reset
          locationSelectedSet(null); // Reset the selected location
          setNumExtraVisibleResults(0); // Reset the number of extra visible results
          if (
            results?.some((result) => isAcipState(result.location.stateProvince)) &&
            ACIP_PRODUCTS.some((product) => productsForSearch?.includes(product)) &&
            !hasSeenAcipModal.current
          ) {
            showAcipModalSet(true);
            hasSeenAcipModal.current = true;
          }
          finderResultsSet({
            allSearchResults: results,
            productsSelected: productsForSearch.split(","),
            place: {
              id: place.id,
              latitude: place.latitude,
              longitude: place.longitude,
              address: place.address
            }
          });
        }
      };

      getLocations();
    } else {
      searchingSet(false);
    }
  }, [
    canSearch,
    country,
    place?.id,
    place?.latitude,
    place?.longitude,
    place?.address,
    productsForSearch,
    searchIndex,
    setHasPanned,
    size,
    selectedVaccinesString,
    triggerFormSubmit
  ]);

  // Set Place and Search Value when Location Search Option changes
  React.useEffect(() => {
    if (locationSearchOption?.label && locationSearchOption?.value) {
      searchValueSet(locationSearchOption.label as string);
      setPlaceFromPlaceId(locationSearchOption.value as string);
    }
  }, [setPlaceFromPlaceId, locationSearchOption?.label, locationSearchOption?.value]);

  // Run new search when filter state changes
  React.useEffect(() => {
    // This makes the page load results faster on First Load, since we don't have to wait for the timeout
    if (!hasRunFirstSearch.current && canSearch) {
      runSearch();
    } else {
      const handler = setTimeout(() => {
        if (canSearch) {
          runSearch();
        }
      }, 1000);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [runSearch, canSearch]);

  const noResultsFound = React.useMemo(() => {
    if (searching || !searchId.current) {
      return false;
    }

    return finderResults.allSearchResults.length === 0;
  }, [searching, finderResults.allSearchResults]);

  const { visibleSearchResults, hasVisibleExpandedResults } = useVisibleSearchResults({
    ...finderResults,
    numExtraVisibleResults
  });

  const setSearchResultsManually = React.useCallback(
    (
      results: FinderLocation[],
      options?: { place?: Place | null; productsSelected?: string[]; searchId?: string }
    ) => {
      finderResultsSet({
        allSearchResults: results,
        productsSelected: options?.productsSelected ?? [],
        place: options?.place ?? null
      });

      if (options?.searchId !== undefined) {
        searchId.current = options.searchId;
      }

      // ensure manual updates mirror the side effects of a completed search
      searchingSet(false);
      hasRunFirstSearch.current = true;
      locationSelectedSet(null);
      setHasPanned(false);
      setNumExtraVisibleResults(0);
    },
    [finderResultsSet, locationSelectedSet, searchingSet, setHasPanned, setNumExtraVisibleResults]
  );

  // Update App State when finder state changes
  useTrackAppState({
    "app-state": {
      "visible-expanded-results": hasVisibleExpandedResults,
      vaccines: selectedVaccinesString
    },
    "num-results-displayed": visibleSearchResults.length,
    "num-results-total": finderResults.allSearchResults.length,
    "view-mode": resultsView
  });

  return (
    <FinderContext.Provider
      value={{
        allSearchResults: finderResults.allSearchResults,
        country,
        formRef,
        hasVisibleExpandedResults,
        isMobile,
        loadMoreTemplate,
        locationHovered,
        locationHoveredSet,
        locationSearchOption,
        locationSearchOptionSet,
        locationSelected,
        locationSelectedSet,
        mapBasedSearch: () => {},
        mobileNavLocalizedHeaders,
        noResultsFound,
        noResultsText,
        place: finderResults.place,
        products,
        productsFromResults: finderResults.productsSelected,
        resultsView,
        resultsViewSet,
        scheduleModalProps,
        scheduleNowTextTemplate,
        searchId: searchId.current,
        searching,
        searchThisAreaText,
        searchValue,
        searchValueSet,
        selectedVaccines,
        selectedVaccinesSet,
        setNumExtraVisibleResults,
        showAcipModal,
        showAcipModalSet,
        showSearchPrompt: !isMobile && hasLoadedQueryParams && !hasRunFirstSearch.current,
        showPharmacyLogos,
        markUserFormInteraction,
        vaccineDropdownOptions,
        vaccineValidationError,
        visibleSearchResults,
        setSearchResultsManually
      }}
    >
      {children}
    </FinderContext.Provider>
  );
};
export const useFinder = () => React.useContext(FinderContext);
